import rawAxios from "axios";
import type { AxiosInstance } from "axios";

// Cloudinary sample video used as a stand-in when uploads are disabled locally.
const DEV_PLACEHOLDER_VIDEO_URL =
  "https://res.cloudinary.com/demo/video/upload/dog.mp4";

export interface PortfolioVideoUploadResult {
  url: string;
  public_id: string | null;
  original_filename: string;
}

export type UploadProgressCallback = (percent: number) => void;

/**
 * Upload a portfolio video directly to Cloudinary.
 *
 * The API only issues a signature; the (large) file is POSTed straight to
 * Cloudinary from the browser, bypassing the through-the-API upload limit.
 * `onProgress` receives a 0–100 percentage while the upload is in flight.
 *
 * @param axios - The app Axios instance (carries the Sanctum bearer), used
 *   only to request the upload signature from the Laravel API.
 * @param candidateUuid - Candidate the portfolio video belongs to.
 * @param file - The video file selected by the user.
 * @param onProgress - Optional 0–100 progress callback.
 */
export async function uploadPortfolioVideo(
  axios: AxiosInstance,
  candidateUuid: string,
  file: File,
  onProgress?: UploadProgressCallback
): Promise<PortfolioVideoUploadResult> {
  const { data: sig } = await axios.post(
    `/candidate/${candidateUuid}/portfolio/sign-video-upload`
  );

  // Local dev: Cloudinary is disabled, so store a placeholder video.
  if (sig?.disabled) {
    return {
      url: DEV_PLACEHOLDER_VIDEO_URL,
      public_id: null,
      original_filename: file.name,
    };
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("api_key", sig.api_key);
  formData.append("timestamp", sig.timestamp);
  formData.append("signature", sig.signature);
  formData.append("public_id", sig.public_id);

  // Use the bare axios (not the app instance) so no Sanctum bearer or
  // Laravel base URL is attached to the cross-origin Cloudinary request.
  const { data } = await rawAxios.post(
    `https://api.cloudinary.com/v1_1/${sig.cloud_name}/video/upload`,
    formData,
    {
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    }
  );

  return {
    url: data.secure_url,
    public_id: data.public_id,
    original_filename: file.name,
  };
}
